/**
 * useCarpoolMatching — hook de matching covoit conducteur ↔ passager.
 *
 * Sprint 6 : pour un couple (conducteur, passager) donné, calcule en temps
 * réel :
 *   - Itinéraire conducteur (domicile → bureau, via Mapbox Directions)
 *   - Détour pour aller chercher le passager (distance domicile passager ↔ route)
 *   - Pickup points suggérés (POI ≥200m du domicile passager, ≤250m de la route)
 *
 * Privacy-first :
 *   - On utilise les coords carpool_zone_lat/lng (déjà arrondies à ~11m
 *     côté serveur, jamais l'adresse exacte)
 *   - Les pickup points sont des POI publics, pas l'adresse du passager
 *
 * Optim audit 2026-05-08 :
 *   - In-memory React Query : staleTime 1h (matching stable, coords figées)
 *   - localStorage 24h : évite re-Mapbox sur reload/navigation (cache key
 *     identique = même home/work/passenger). Économie ~80 % Tilequery
 *     quota après semaine 1.
 *
 * @see src/lib/mapbox/matching.ts (calculs géométriques)
 * @see src/lib/mapbox/pickupPoints.ts (génération POI)
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchDirections,
  fetchDirectionsVia,
  type LngLat,
  type DirectionsProfile,
} from '@/lib/mapbox/client';
import {
  computeMatchingScore,
  polylineLengthM,
  polylineLengthUpToIndexM,
  type LngLatTuple,
} from '@/lib/mapbox/matching';
import { generatePickupPoints, type PickupPoint } from '@/lib/mapbox/pickupPoints';
import {
  computeDetourMinutes,
  computeDetourKm,
  computeDetourScore,
} from '@/lib/carpool/detourScore';
import { logger } from '@/services/logger';

/** Vitesse de marche pour convertir une distance pickup en minutes (~4.8 km/h). */
const WALK_METERS_PER_MINUTE = 80;

export interface CarpoolMatchInput {
  driverHome: LngLat;
  driverWork: LngLat;
  passengerHome: LngLat;
  /**
   * Heure de départ du conducteur (format "HH:MM" ou "HH:MM:SS").
   * Optionnel — si fourni, permet de calculer `estimatedPickupTime`.
   * Source : `carpool_availability.departure_time`.
   */
  departureTime?: string;
}

export interface CarpoolMatchResult {
  /**
   * Score composite 0..1 (détour réel + marche + proximité route). 1 = idéal.
   * Même formule que le serveur (`computeDetourScore`) → l'aperçu reflète le
   * classement réel des propositions.
   */
  score: number;
  /** True si le match passe les garde-fous (détour ≤ max, marche ≤ max). */
  accepted: boolean;
  /** Détour conducteur RÉEL en minutes (route via passager − route directe). */
  detourMinutes: number;
  /** Détour conducteur RÉEL en km. */
  detourKm: number;
  /** Distance perpendiculaire domicile passager ↔ route (m) — signal géométrique secondaire. */
  detourM: number;
  /** Longueur totale de la route conducteur, en km. */
  routeKm: number;
  /** Pickup points suggérés (max 3). Vide si pas de POI viables. */
  pickupPoints: PickupPoint[];
  /** Polyline de la route, utile pour le rendu carte. */
  routePolyline: LngLatTuple[];
  /**
   * Heure estimée d'arrivée du conducteur au pickup point passager,
   * au format "HH:MM" (ex : "08:17"). Null si `departureTime` non fourni
   * ou si le calcul est impossible. Calculé via :
   *   fraction = distance(driverHome → segmentIndex) / routeDistance
   *   ETA = departureTime + fraction × routeDurationS
   */
  estimatedPickupTime: string | null;
}

/**
 * Clé de cache stable (arrondie au 100m près) — évite les re-fetch à
 * chaque pixel de drift GPS.
 */
function makeCacheKey(input: CarpoolMatchInput): string {
  const round = (n: number) => Math.round(n * 1000) / 1000; // 3 décimales (~111m)
  return [
    round(input.driverHome.lat),
    round(input.driverHome.lng),
    round(input.driverWork.lat),
    round(input.driverWork.lng),
    round(input.passengerHome.lat),
    round(input.passengerHome.lng),
  ].join(',');
}

const LS_PREFIX = 'koken:carpool-matching:';
const LS_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const LS_VERSION = 3; // bumped 2026-07-01 : score composite détour réel (A2)

interface PersistedMatch {
  v: number;
  savedAt: number;
  result: CarpoolMatchResult;
}

function readPersistedMatch(key: string): CarpoolMatchResult | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedMatch;
    if (parsed.v !== LS_VERSION) {
      window.localStorage.removeItem(LS_PREFIX + key);
      return null;
    }
    if (Date.now() - parsed.savedAt > LS_TTL_MS) {
      window.localStorage.removeItem(LS_PREFIX + key);
      return null;
    }
    return parsed.result;
  } catch {
    return null;
  }
}

function writePersistedMatch(key: string, result: CarpoolMatchResult): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: PersistedMatch = {
      v: LS_VERSION,
      savedAt: Date.now(),
      result,
    };
    window.localStorage.setItem(LS_PREFIX + key, JSON.stringify(payload));
  } catch (err) {
    // localStorage plein ou indisponible — on ignore, c'est un cache opportuniste
    logger.debug('[useCarpoolMatching] localStorage write failed', { err });
  }
}

/**
 * P0 #8 fix audit pré-pilote 2026-05-08 — Clear le cache localStorage du
 * matching covoit. À appeler au signOut pour éviter qu'un utilisateur suivant
 * sur le même device (device partagé, kiosk, prêt) puisse récupérer le cache
 * qui contient les coords approximatives (~110m via cache key 3 décimales) des
 * collègues du précédent compte connecté.
 */
export function clearCarpoolMatchingCache(): number {
  if (typeof window === 'undefined') return 0;
  let cleared = 0;
  try {
    // Itère envers (length-1 → 0) parce que removeItem décale les indices
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(LS_PREFIX)) {
        window.localStorage.removeItem(key);
        cleared++;
      }
    }
  } catch (err) {
    logger.debug('[useCarpoolMatching] clearCarpoolMatchingCache failed', { err });
  }
  return cleared;
}

/**
 * Calcule l'heure d'arrivée au pickup (format "HH:MM") à partir de
 * l'heure de départ ("HH:MM" ou "HH:MM:SS") et d'un décalage en secondes.
 * Gère le passage minuit (rare sur un trajet domicile-bureau).
 */
function addSecondsToTime(timeStr: string, seconds: number): string {
  const parts = timeStr.split(':').map(Number);
  const [h = 0, m = 0, s = 0] = parts;
  const totalSec = h * 3600 + m * 60 + s + Math.round(seconds);
  const hOut = Math.floor(totalSec / 3600) % 24;
  const mOut = Math.floor((totalSec % 3600) / 60);
  return `${String(hOut).padStart(2, '0')}:${String(mOut).padStart(2, '0')}`;
}

/**
 * Construit un `depart_at` ISO 8601 à partir d'une heure "HH:MM" pour l'ETA
 * trafic (Mapbox driving-traffic). Vise le prochain créneau : aujourd'hui à
 * cette heure, ou demain si déjà passée. Renvoie undefined si format invalide.
 */
function buildDepartAtIso(timeStr: string): string | undefined {
  const [h, m] = timeStr.split(':').map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) return undefined;
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
  if (d.getTime() < now.getTime()) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

export function useCarpoolMatching(
  input: CarpoolMatchInput | null,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['carpool-matching', input ? makeCacheKey(input) : null],
    enabled: enabled && !!input,
    // Optim audit 2026-05-08 : staleTime 1h (au lieu de 10 min). Les coords
    // home/work/passenger sont figées, donc on peut tenir 1h sans refetch.
    staleTime: 60 * 60 * 1000,
    gcTime: 4 * 60 * 60 * 1000,
    queryFn: async (): Promise<CarpoolMatchResult | null> => {
      if (!input) return null;

      // Optim audit 2026-05-08 : check localStorage avant de hit Mapbox.
      // Cache 24h pour les mêmes coords. Économie ~80% Tilequery quota
      // après semaine 1 (mêmes paires conducteur/passager se ré-affichent).
      const cacheKey = makeCacheKey(input);
      const persisted = readPersistedMatch(cacheKey);
      if (persisted) {
        return persisted;
      }

      // 1. Route conducteur directe — ETA trafic (driving-traffic + depart_at)
      // quand on connaît l'heure de départ, sinon driving. Fallback driving si
      // l'API trafic échoue (indispo / hors zone). On mémorise le profil
      // effectivement utilisé pour que le détour se calcule dans les mêmes
      // conditions (baseline et via comparables).
      const departAt = input.departureTime ? buildDepartAtIso(input.departureTime) : undefined;
      let usedProfile: DirectionsProfile = 'driving';
      let route = departAt
        ? await fetchDirections(input.driverHome, input.driverWork, 'driving-traffic', { departAt })
        : null;
      if (route) {
        usedProfile = 'driving-traffic';
      } else {
        route = await fetchDirections(input.driverHome, input.driverWork, 'driving');
        usedProfile = 'driving';
      }
      if (!route) return null;

      const polyline = route.geometry.coordinates as LngLatTuple[];

      // 2. Géométrie brute (proximité perpendiculaire) — désormais signal
      // secondaire + proxy d'overlap pour le score composite (le serveur, lui,
      // affine avec l'overlap turf réel).
      const matching = computeMatchingScore(input.passengerHome, polyline);

      // 3. Pickup points (nécessite la polyline).
      const pickupPoints = await generatePickupPoints(input.passengerHome, polyline);

      // 4. VRAI détour conducteur : route(driverHome → passager → driverWork)
      // moins route directe, dans le même profil/heure. A2 — unification avec le
      // scoring serveur via la formule partagée `computeDetourScore`.
      const viaRoute = await fetchDirectionsVia(
        input.driverHome,
        input.passengerHome,
        input.driverWork,
        usedProfile,
        usedProfile === 'driving-traffic' ? { departAt } : {},
      );
      const detourMinutes = viaRoute
        ? computeDetourMinutes(route.durationSec, viaRoute.durationSec)
        : 0;
      const detourKm = viaRoute
        ? computeDetourKm(route.distanceM, viaRoute.distanceM)
        : 0;

      // Marche passager → meilleur pickup (proxy distance/vitesse de marche).
      const bestPickup = pickupPoints[0];
      const passengerWalkMinutes = bestPickup
        ? bestPickup.distanceFromHomeM / WALK_METERS_PER_MINUTE
        : undefined;

      const scored = computeDetourScore({
        detourMinutes,
        // proxy d'overlap côté client (proximité route) ; le serveur utilise
        // l'overlap turf exact. Le terme dominant (détour) est identique.
        overlapPct: matching.score,
        passengerWalkMinutes,
      });

      // ETA pickup : fraction de route parcourue avant le segment le plus
      // proche du passager × durée totale (trafic si driving-traffic) + départ.
      const totalLengthM = polylineLengthM(polyline);
      const pickupLengthM = polylineLengthUpToIndexM(polyline, matching.segmentIndex);
      const fraction = totalLengthM > 0 ? pickupLengthM / totalLengthM : 0;
      const pickupDurationS = fraction * route.durationSec;
      const estimatedPickupTime = input.departureTime
        ? addSecondsToTime(input.departureTime, pickupDurationS)
        : null;

      const result: CarpoolMatchResult = {
        score: scored.score,
        accepted: scored.accepted,
        detourMinutes: Math.round(detourMinutes * 10) / 10,
        detourKm: Math.round(detourKm * 100) / 100,
        detourM: matching.detourM,
        routeKm: totalLengthM / 1000,
        pickupPoints,
        routePolyline: polyline,
        estimatedPickupTime,
      };

      writePersistedMatch(cacheKey, result);
      return result;
    },
  });
}
